import { hasValue } from "../data";
import { F } from "../gerencialFields";
import { toDate } from "../status";

export const ETAPA = {
  COMPRAS: "COMPRAS",
  MAQUINAS: "MAQUINAS",
  TERCEIROS: "TERCEIROS",
  MARCENARIA: "MARCENARIA",
  ACABAMENTO: "ACABAMENTO",
  EXPEDICAO: "EXPEDICAO",
};

export const ORDEM_ETAPAS = [
  ETAPA.COMPRAS,
  ETAPA.MAQUINAS,
  ETAPA.TERCEIROS,
  ETAPA.MARCENARIA,
  ETAPA.ACABAMENTO,
  ETAPA.EXPEDICAO,
];

export const ETAPA_LABELS = {
  [ETAPA.COMPRAS]: "Compras",
  [ETAPA.MAQUINAS]: "Máquinas",
  [ETAPA.TERCEIROS]: "Terceiros",
  [ETAPA.MARCENARIA]: "Marcenaria",
  [ETAPA.ACABAMENTO]: "Acabamento",
  [ETAPA.EXPEDICAO]: "Expedição",
};

const TERCEIROS_FIELDS = [
  F.SERRALHERIA_TERCEIROS,
  F.PERFIL_ALUMINIO_TERCEIROS,
  F.PORTAS_FRENTES_TERCEIROS,
  F.VIDRACARIA,
  F.TAPECARIA_TERCEIROS,
  F.FERRAGENS_ESPECIAIS_TERCEIROS,
];

const ACABAMENTO_FIELDS = [
  F.PINTURA,
  F.VERNIZ_ACAB,
  F.PINTURA_PERFIL,
  F.PINTURA_SERRALHERIA,
];

function isXOuVazio(value) {
  if (!hasValue(value)) return true;
  const text = String(value).trim().toUpperCase();
  if (text === "X") return true;
  // "0" significa "zero unidades" — o ambiente não precisa desse item.
  // Tratar como vazio para não inflar contagens em telas como
  // /acabamento e /terceiros.
  if (text === "0") return true;
  return false;
}

function temAlgumItem(amb, fields) {
  return fields.some((field) => !isXOuVazio(amb?.[field]));
}

export function precisaTerceiros(amb) {
  return temAlgumItem(amb, TERCEIROS_FIELDS);
}

export function precisaAcabamento(amb) {
  return temAlgumItem(amb, ACABAMENTO_FIELDS);
}

// IMPORTANTE: usamos APENAS colunas reais (digitadas pelos operadores conforme
// os eventos acontecem). As colunas BG (FINAL_MAQUINAS), BX (FINAL_MARCENARIA),
// CO (ENTREGA_CLIENTE), CF (FINAL_ACABAMENTO) e BP (ENTREGA_TERCEIROS) na
// planilha são fórmulas WORKDAY que calculam previsão a partir da APROVAÇÃO —
// elas ficam preenchidas mesmo quando o evento ainda não aconteceu, então
// NÃO podem ser usadas como sinal de "saiu da etapa".

export function saidaCompras(amb) {
  // Saída de Compras = MDF/BORDO chegou na fábrica (data digitada).
  return toDate(amb?.[F.MDF_BORDO]);
}

export function saidaMaquinas(amb) {
  // Saída de Máquinas = última operação real do bloco (Corte/Bordo/Furação/Usinagem).
  // Usinagem é a última na sequência produtiva. Caímos para as anteriores se ela faltar.
  return (
    toDate(amb?.[F.DATA_DE_USINAGEM]) ||
    toDate(amb?.[F.DATA_DE_FURACAO]) ||
    toDate(amb?.[F.DATA_DE_BORDO]) ||
    toDate(amb?.[F.DATA_DE_CORTE])
  );
}

export function saidaTerceiros(amb) {
  if (!precisaTerceiros(amb)) {
    return saidaMaquinas(amb) || saidaCompras(amb);
  }
  return toDate(amb?.[F.RECEBIMENTO_TERCEIROS]);
}

export function saidaMarcenaria(amb) {
  return toDate(amb?.[F.MARCENARIA_TERMINO]);
}

export function saidaAcabamento(amb) {
  if (!precisaAcabamento(amb)) {
    return saidaMarcenaria(amb);
  }
  // CE (ACABAMENTO REALIZADO) é o evento real. CH (LIMPEZA/EMBALAGEM) também é
  // real e marca que o ambiente passou pela cabine — usamos como fallback.
  return (
    toDate(amb?.[F.ACABAMENTO_REALIZADO]) ||
    toDate(amb?.[F.LIMPEZA_EMBALAGEM])
  );
}

export function saidaExpedicao(amb) {
  // Saída de Expedição = ambiente carregado no caminhão (saiu da fábrica).
  // CN (CARREGAMENTO) é a data real digitada. CO (ENTREGA_CLIENTE) é fórmula
  // de previsão que SEMPRE fica preenchida quando há aprovação, então não
  // pode ser usada aqui.
  return toDate(amb?.[F.CARREGAMENTO]);
}

const SAIDAS_POR_ETAPA = {
  [ETAPA.COMPRAS]: saidaCompras,
  [ETAPA.MAQUINAS]: saidaMaquinas,
  [ETAPA.TERCEIROS]: saidaTerceiros,
  [ETAPA.MARCENARIA]: saidaMarcenaria,
  [ETAPA.ACABAMENTO]: saidaAcabamento,
  [ETAPA.EXPEDICAO]: saidaExpedicao,
};

export function saidaEtapa(etapa, amb) {
  const fn = SAIDAS_POR_ETAPA[etapa];
  return fn ? fn(amb) : null;
}

export function entradaEtapa(etapa, amb) {
  const idx = ORDEM_ETAPAS.indexOf(etapa);
  if (idx < 0) return null;
  if (idx === 0) {
    return toDate(amb?.[F.APROVACAO_DO_EXECUTIVO]);
  }
  if (etapa === ETAPA.MARCENARIA) {
    const inicio = toDate(amb?.[F.MARCENARIA_INICIO]);
    if (inicio) return inicio;
  }
  if (etapa === ETAPA.EXPEDICAO) {
    const limpeza = toDate(amb?.[F.LIMPEZA_EMBALAGEM]);
    if (limpeza) return limpeza;
  }
  return saidaEtapa(ORDEM_ETAPAS[idx - 1], amb);
}

export function estaEm(etapa, amb) {
  const entrada = entradaEtapa(etapa, amb);
  if (!entrada) return false;
  return !saidaEtapa(etapa, amb);
}

export function getEtapaAtual(amb) {
  if (saidaExpedicao(amb)) return null;
  for (const etapa of ORDEM_ETAPAS) {
    if (estaEm(etapa, amb)) return etapa;
  }
  return null;
}
